import type { ILanguageResources, ITranslationModule } from '../../../interfaces';
import { interpolate } from '../../../global';

class Id {
  private constructor() {}
  public static readonly prefix = 'UI.scripts.quotes.v6.V6QuoteItemFrameView.';
  public static readonly supplierOfflineReadonly = `${Id.prefix}supplierOfflineReadonly`;
  public static readonly propertiesSectionTitle = `${Id.prefix}propertiesSectionTitle`;
  public static readonly frameTitle = `${Id.prefix}frameTitle`;
  public static readonly description = `${Id.prefix}description`;
  public static readonly quantity = `${Id.prefix}quantity`;
  public static readonly notesToClient = `${Id.prefix}notesToClient`;
  public static readonly notesToClientTooltip = `${Id.prefix}notesToClientTooltip`;
  public static readonly notesToManufacturer = `${Id.prefix}notesToManufacturer`;
  public static readonly notesToManufacturerTooltip = `${Id.prefix}notesToManufacturerTooltip`;
  public static readonly discardFrameChanges = `${Id.prefix}discardFrameChanges`;
  public static readonly newFrame = `${Id.prefix}newFrame`;
  public static readonly invalidQuantity = `${Id.prefix}invalidQuantity`;
  public static readonly pleaseWaitUntilProcessingComplete = `${Id.prefix}pleaseWaitUntilProcessingComplete`;
  public static readonly editingFrame = `${Id.prefix}editingFrame`;
  public static readonly preparingToSave = `${Id.prefix}preparingToSave`;
  public static readonly preparingToSaveWithSupplier = `${Id.prefix}preparingToSaveWithSupplier`;
  public static readonly prepareInformationForSupplier = `${Id.prefix}prepareInformationForSupplier`;
  public static readonly updatingQuotePrice = `${Id.prefix}updatingQuotePrice`;
  public static readonly requestingUpdatesFromSupplier = `${Id.prefix}requestingUpdatesFromSupplier`;
  public static readonly updatingQuoteWithSupplierChanges = `${Id.prefix}updatingQuoteWithSupplierChanges`;
  public static readonly savingSendingUpdates = `${Id.prefix}savingSendingUpdates`;
  public static readonly savingCompleted = `${Id.prefix}savingCompleted`;
  public static readonly reference = `${Id.prefix}reference`;
  public static readonly viewNote = `${Id.prefix}viewNote`;
  public static readonly addNote = `${Id.prefix}addNote`;
  public static readonly editNote = `${Id.prefix}editNote`;
  public static readonly groupAssignment = `${Id.prefix}groupAssignment`;
  public static readonly createGroupOption = `${Id.prefix}createGroupOption`;
  public static readonly groupPlaceholder = `${Id.prefix}groupPlaceholder`;
  public static readonly noGroupsYet = `${Id.prefix}noGroupsYet`;
}

export class LangV6QuoteItemFrameView {
  private constructor() {}
  public static supplierOfflineReadonly(): string {
    return interpolate(Id.supplierOfflineReadonly);
  }
  public static propertiesSectionTitle(): string {
    return interpolate(Id.propertiesSectionTitle);
  }
  public static frameTitle(): string {
    return interpolate(Id.frameTitle);
  }
  public static description(): string {
    return interpolate(Id.description);
  }
  public static quantity(): string {
    return interpolate(Id.quantity);
  }
  public static notesToClient(): string {
    return interpolate(Id.notesToClient);
  }
  public static notesToClientTooltip(): string {
    return interpolate(Id.notesToClientTooltip);
  }
  public static notesToManufacturer(): string {
    return interpolate(Id.notesToManufacturer);
  }
  public static notesToManufacturerTooltip(): string {
    return interpolate(Id.notesToManufacturerTooltip);
  }
  public static discardFrameChanges(): string {
    return interpolate(Id.discardFrameChanges);
  }
  public static newFrame(): string {
    return interpolate(Id.newFrame);
  }
  public static invalidQuantity(params: { quantity: number }): string {
    return interpolate(Id.invalidQuantity, params);
  }
  public static pleaseWaitUntilProcessingComplete(): string {
    return interpolate(Id.pleaseWaitUntilProcessingComplete);
  }
  public static editingFrame(params: { title: string }): string {
    return interpolate(Id.editingFrame, params);
  }
  public static preparingToSave(params: { title: string }): string {
    return interpolate(Id.preparingToSave, params);
  }
  public static preparingToSaveWithSupplier(params: { title: string }): string {
    return interpolate(Id.preparingToSaveWithSupplier, params);
  }
  public static prepareInformationForSupplier(params: { title: string }): string {
    return interpolate(Id.prepareInformationForSupplier, params);
  }
  public static updatingQuotePrice(params: { title: string }): string {
    return interpolate(Id.updatingQuotePrice, params);
  }
  public static requestingUpdatesFromSupplier(params: { title: string }): string {
    return interpolate(Id.requestingUpdatesFromSupplier, params);
  }
  public static updatingQuoteWithSupplierChanges(params: { title: string }): string {
    return interpolate(Id.updatingQuoteWithSupplierChanges, params);
  }
  public static savingSendingUpdates(params: { title: string }): string {
    return interpolate(Id.savingSendingUpdates, params);
  }
  public static savingCompleted(params: { title: string }): string {
    return interpolate(Id.savingCompleted, params);
  }
  public static reference(): string {
    return interpolate(Id.reference);
  }
  public static viewNote(): string {
    return interpolate(Id.viewNote);
  }
  public static addNote(): string {
    return interpolate(Id.addNote);
  }
  public static editNote(): string {
    return interpolate(Id.editNote);
  }
  public static groupAssignment(): string {
    return interpolate(Id.groupAssignment);
  }
  public static createGroupOption(params: { name: string }): string {
    return interpolate(Id.createGroupOption, params);
  }
  public static groupPlaceholder(): string {
    return interpolate(Id.groupPlaceholder);
  }
  public static noGroupsYet(): string {
    return interpolate(Id.noGroupsYet);
  }
}

export class V6QuoteItemFrameViewModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(
      Id.supplierOfflineReadonly,
      "{{glossary 'frame'}} is opening readonly because the {{glossary 'supplier'}} is currently offline. please try again soon",
      'Message when supplier is offline and frame is readonly'
    );
    register.add(Id.propertiesSectionTitle, 'Properties', 'Section title for properties');
    register.add(Id.frameTitle, "{{glossary 'frame-title'}}", 'Label for frame title');
    register.add(Id.description, 'Description', 'Label for description');
    register.add(Id.quantity, 'Quantity', 'Label for quantity');
    register.add(Id.notesToClient, "Notes to {{glossary 'client'}}", 'Label for notes to client');
    register.add(
      Id.notesToClientTooltip,
      "This detail is published on {{glossary 'quote'}} Reports.",
      'Tooltip for notes to client'
    );
    register.add(Id.notesToManufacturer, 'Notes to Manufacturer', 'Label for notes to manufacturer');
    register.add(
      Id.notesToManufacturerTooltip,
      "This detail is published on the {{glossary 'purchase-order'}} report and is added to V6 when the {{glossary 'purchase-order'}} is issued.",
      'Tooltip for notes to manufacturer'
    );
    register.add(Id.discardFrameChanges, "Discard {{glossary 'frame'}} Changes", 'Label for discard frame changes');
    register.add(Id.newFrame, "New {{glossary 'frame'}}", 'Label for new frame');
    register.add(Id.invalidQuantity, 'Quantity of {{quantity}} is invalid', 'Error message for invalid quantity');
    register.add(
      Id.pleaseWaitUntilProcessingComplete,
      'please wait until processing is complete and try again',
      'Message for waiting until processing is complete'
    );
    register.add(Id.editingFrame, "# Editing {{glossary 'frame'}}\n\n## {{title}}  ", 'Header for editing frame');
    register.add(Id.preparingToSave, '# Preparing to save "{{title}}"  ', 'Header for preparing to save');
    register.add(
      Id.preparingToSaveWithSupplier,
      "# Preparing to save \"{{title}}\"  \n\n+ Getting {{glossary 'frame'}} updates from {{glossary 'supplier'}}\n                ",
      'Header for preparing to save with supplier'
    );
    register.add(
      Id.prepareInformationForSupplier,
      '# Preparing to save "{{title}}"  \n\n+ Prepare information for {{glossary \'supplier\'}} processing\n                ',
      'Header for preparing information for supplier'
    );
    register.add(
      Id.updatingQuotePrice,
      '# Preparing to save "{{title}}"  \n\n+ updating {{glossary \'quote\'}} price\n                ',
      'Header for updating quote price'
    );
    register.add(
      Id.requestingUpdatesFromSupplier,
      '# Preparing to save "{{title}}"  \n\n+ requesting updates from {{glossary \'supplier\'}}\n                ',
      'Header for requesting updates from supplier'
    );
    register.add(
      Id.updatingQuoteWithSupplierChanges,
      "# Preparing to save \"{{title}}\"  \n\n+ Updating {{glossary 'quote'}} with {{glossary 'supplier'}} changes\n                ",
      'Header for updating quote with supplier changes'
    );
    register.add(
      Id.savingSendingUpdates,
      '# Saving "{{title}}"  \n\n+ Sending updates\n                ',
      'Header for saving and sending updates'
    );
    register.add(
      Id.savingCompleted,
      '# Saving "{{title}}"  \n\n+ Completed\n                ',
      'Header for saving completed'
    );
    register.add(Id.reference, 'Reference', 'Label for reference field');
    register.add(Id.viewNote, 'View Note', 'Button label for viewing a note');
    register.add(Id.addNote, 'Add Note', 'Button label for adding a note');
    register.add(Id.editNote, 'Edit Note', 'Button label for editing a note');
    register.add(Id.groupAssignment, 'Group', 'Label for the group assignment dropdown on the item editor');
    register.add(
      Id.createGroupOption,
      'Create group "{{name}}"',
      'Combobox row to create a new group with the typed name'
    );
    register.add(
      Id.groupPlaceholder,
      'Select or type to create...',
      'Placeholder for the group combobox when no group is assigned'
    );
    register.add(
      Id.noGroupsYet,
      'No groups created yet',
      'Group combobox empty-listbox text when the quote has no groups'
    );
  }
}
